/**
 * Sprint DN : récupération automatique après un déploiement.
 *
 * Le back-office est découpé en chunks JS au nom hashé (code-splitting par page
 * via React.lazy → ex `UsersPage-B0JFZlhS.js`). À chaque déploiement, les hashs
 * changent et Cloudflare Pages ne sert QUE le dernier build : les anciens chunks
 * renvoient 404. Un onglet resté ouvert (ou un `index.html` en cache) datant
 * d'avant le déploiement échoue alors à charger une page lazy →
 * « Failed to fetch dynamically imported module ».
 *
 * Remède : détecter ce cas précis et recharger la page UNE fois (garde
 * anti-boucle via sessionStorage) pour récupérer le `index.html` frais, qui
 * pointe vers les nouveaux chunks.
 */

const RELOAD_GUARD_KEY = "alto:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15_000;

/** Vrai si l'erreur correspond à un chunk dynamique introuvable (post-déploiement). */
export function isStaleChunkError(err: unknown): boolean {
  const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    msg.includes("failed to import") ||
    (msg.includes("dynamically imported module") && msg.includes("module"))
  );
}

/**
 * Recharge la page une seule fois (cooldown) pour éviter toute boucle si le
 * rechargement ne résout pas le problème (ex : réellement hors-ligne).
 * Retourne true si un reload a été déclenché.
 */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "0");
    if (Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS) {
      return false; // déjà rechargé il y a < 15 s → on n'insiste pas
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    /* sessionStorage indisponible : on recharge quand même une fois */
  }
  window.location.reload();
  return true;
}

// ---------------------------------------------------------------------------
// Sprint DO-1 : mise à jour PROACTIVE au retour de focus.
//
// Le correctif DN ne récupère qu'au moment où une page lazy échoue à charger
// (clic → 404 → reload). Ici on va plus loin : quand l'onglet du back-office
// redevient visible après une absence (le matin, par ex.), on vérifie si un
// nouveau build a été déployé et on recharge AVANT que l'utilisateur ne clique
// — donc sans jamais voir l'écran d'erreur.
//
// Détection : on compare le nom hashé du bundle d'entrée chargé dans la page
// (`/assets/index-XXXX.js`) à celui présent dans le `index.html` actuellement
// servi. S'ils diffèrent → un déploiement a eu lieu.
// ---------------------------------------------------------------------------

const ENTRY_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;
const VERSION_CHECK_THROTTLE_MS = 30_000;

let loadedEntrySrc: string | null = null;
let lastVersionCheck = 0;

function getLoadedEntrySrc(): string | null {
  if (loadedEntrySrc) return loadedEntrySrc;
  if (typeof document === "undefined") return null;
  const el = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  );
  loadedEntrySrc = el?.getAttribute("src") ?? null;
  return loadedEntrySrc;
}

/** Ne pas recharger si l'utilisateur est en train de saisir → on perdrait sa frappe. */
function isUserEditing(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    el.isContentEditable === true
  );
}

async function isNewVersionDeployed(): Promise<boolean> {
  const current = getLoadedEntrySrc();
  if (!current) return false;
  try {
    // `no-store` : on force le réseau pour obtenir le index.html à jour.
    const res = await fetch("/", { cache: "no-store" });
    if (!res.ok) return false;
    const html = await res.text();
    const m = html.match(ENTRY_RE);
    return m ? m[0] !== current : false;
  } catch {
    return false;
  }
}

/**
 * Installe la surveillance de version : au retour de focus/visibilité (throttlé
 * à 30 s), si un nouveau build est en ligne et que l'utilisateur n'est pas en
 * train de saisir, recharge la page pour basculer sur la nouvelle version.
 */
export function installVersionWatcher(): void {
  const check = async () => {
    if (typeof document === "undefined") return;
    if (document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastVersionCheck < VERSION_CHECK_THROTTLE_MS) return;
    lastVersionCheck = now;
    if (!(await isNewVersionDeployed())) return;
    if (isUserEditing()) return; // on réessaiera au prochain retour de focus
    reloadForStaleChunk();
  };
  document.addEventListener("visibilitychange", () => void check());
  window.addEventListener("focus", () => void check());
}
